import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, CircularProgress, FormHelperText, IconButton, MenuItem, Typography } from '@mui/material';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  TextField,
  CustomSelect,
  CustomCheckbox,
  CustomButton,
} from '../../../../v2/MUI_custom_component';
import GlassModal from '../../../../v2/MUI_custom_component/Modal/GlassModal';
import ModalList from '../../../../v2/MUI_custom_component/Modal/ModalList';
import { validateIsEmailAddress } from '../../../../v2/Utils';
import {
  initiateDigiLocker,
  fetchDigiLockerData,
  sendBeneficiaryOtp,
  verifyBeneficiaryOtp,
  verifyUserPan,
} from '../APIs/action';
import DigiLockerResultCard from './DigiLockerResultCard';
import OTPVerificationComponent from './OTPVerificationComponent';
import { setGapBeneficiaryData } from '../reducer/gapBeneficiaryReducer';
import { useNavigate } from 'react-router-dom';

const AddNpsBeneficiary = ({
  insuranceData,
  owner_type_text,
  owner_list,
  confirmation_text_for_digilocker,
  returnTransactionId,
  onBeforeDigiLockerRedirect,
  savedFormData,
  onFormActivity,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedOwner, setSelectedOwner] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [emailId, setEmailId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [digiLockerData, setDigiLockerData] = useState(null);
  const [digiLockerLoading, setDigiLockerLoading] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [panNumber, setPanNumber] = useState(insuranceData?.firm_pan || '');
  const [panVerified, setPanVerified] = useState(false);
  const [panError, setPanError] = useState(null);
  const [panVerifying, setPanVerifying] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [kycConsentModalOpen, setKycConsentModalOpen] = useState(false);

  const formatDobDisplay = (dob) => {
    if (!dob) return '-';
    const [yyyy, mm, dd] = dob.split('-');
    return new Date(`${yyyy}-${mm}-${dd}`).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const calculateAge = (dob) => {
    const [yyyy, mm, dd] = dob.split('-');
    const birth = new Date(`${yyyy}-${mm}-${dd}`);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const formattedGender = digiLockerData?.gender
    ? digiLockerData.gender.charAt(0) +
      digiLockerData.gender.slice(1).toLowerCase()
    : '-';
  const age = digiLockerData?.date_of_birth
    ? calculateAge(digiLockerData.date_of_birth)
    : null;

  const selectedOwnerLabel =
    owner_list?.find((o) => o.value === selectedOwner)?.label ||
    owner_list?.[0]?.label ||
    '';

  const namesMismatch =
    !!digiLockerData &&
    !!selectedOwnerLabel &&
    digiLockerData.name?.trim().toLowerCase() !== selectedOwnerLabel.trim().toLowerCase();

  const isProprietorship = owner_type_text?.label?.toLowerCase().includes('proprietor');

  const isMultiplePartnerMismatch = namesMismatch && !isProprietorship && owner_list?.length > 1;

  const mismatchMessage = isProprietorship
    ? "Proprietor's name doesn't match DigiLocker records. Ensure the correct proprietor is verified."
    : owner_list?.length <= 1
    ? "Partner's name doesn't match DigiLocker records. Ensure the correct partner is verified."
    : "Partner's name doesn't match the data in DigiLocker. Please select a different partner.";

  const checkboxLabel = (
    <Typography variant='body1'>
      I accept the{' '}
      <span
        style={{ textDecoration: 'underline', cursor: 'pointer' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setKycConsentModalOpen(true);
        }}
      >
        KYC Consent
      </span>
    </Typography>
  );

  // Restore form state after DigiLocker redirect
  useEffect(() => {
    if (!savedFormData) return;
    if (savedFormData.mobileNumber) setMobileNumber(savedFormData.mobileNumber);
    if (savedFormData.emailId) setEmailId(savedFormData.emailId);
    if (savedFormData.selectedOwner)
      setSelectedOwner(savedFormData.selectedOwner);
    if (savedFormData.termsAccepted)
      setTermsAccepted(savedFormData.termsAccepted);
    if (savedFormData.phoneVerified)
      setPhoneVerified(savedFormData.phoneVerified);
  }, [savedFormData]);

  // Auto-fetch DigiLocker data when returning from redirect
  useEffect(() => {
    if (!returnTransactionId) return;
    setDigiLockerLoading(true);
    fetchDigiLockerData(returnTransactionId).then((res) => {
      setDigiLockerLoading(false);
      if (!res?.error && res?.data) {
        setDigiLockerData(res.data);
        onFormActivity?.();
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          digiLocker: 'Failed to fetch DigiLocker data. Please try again.',
        }));
      }
    });
  }, [returnTransactionId]);

  const handleVerifyPhone = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      setValidationErrors((prev) => ({
        ...prev,
        mobileNumber: 'Please enter a valid 10-digit phone number',
      }));
      return;
    }
    setOtpSending(true);
    const res = await sendBeneficiaryOtp(mobileNumber);
    setOtpSending(false);
    if (!res?.success) {
      setValidationErrors((prev) => ({
        ...prev,
        mobileNumber: res?.message || 'Failed to send OTP. Please try again.',
      }));
      return;
    }
    setOtpModalOpen(true);
  };

  const handleVerifyPan = async () => {
    const ownerName =
      owner_list?.find((o) => o.value === selectedOwner)?.label ||
      owner_list?.[0]?.label ||
      '';
    const firmType = owner_type_text?.value;

    setPanVerifying(true);
    const res = await verifyUserPan({
      user_details: {
        name: ownerName,
        mobile_no: mobileNumber,
        email: emailId,
      },
      user_details_from_digilocker: {
        name: digiLockerData?.name,
        gender: digiLockerData?.gender,
        dob: digiLockerData?.date_of_birth,
        pan: digiLockerData?.pancard_no || null,
        document_id: digiLockerData?.document_no,
      },
      user_pan: panNumber,
      firm_type: firmType,
    });
    setPanVerifying(false);

    if (res?.data?.is_user_pan_verified) {
      setPanVerified(true);
      setPanError(null);
    } else {
      setPanError(
        res?.data?.error_message ||
          res?.message ||
          'PAN verification failed. Please try again.',
      );
    }
  };

  const handleVerifyWithDigiLocker = async () => {
    if (!/^\d{10}$/.test(mobileNumber)) {
      setValidationErrors((prev) => ({
        ...prev,
        mobileNumber: 'Please enter a valid 10-digit phone number',
      }));
      return;
    }

    setDigiLockerLoading(true);
    const res = await initiateDigiLocker(mobileNumber);
    setDigiLockerLoading(false);

    if (res?.error || !res?.data) {
      setValidationErrors((prev) => ({
        ...prev,
        digiLocker: 'Failed to initiate DigiLocker. Please try again.',
      }));
      return;
    }

    // Save parent state before navigating away (lost on full-page redirect)
    onBeforeDigiLockerRedirect?.(res.data.session_id, {
      mobileNumber,
      emailId,
      selectedOwner,
      termsAccepted,
      phoneVerified,
    });

    // Full-page redirect — works inside WebViews where window.open() is blocked
    window.location.href = res.data.redirect_url;
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant='h3'>Add Beneficiary for NPS</Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {owner_list && owner_list.length > 1 ? (
            <CustomSelect
              size={'small'}
              width={'100%'}
              label={owner_type_text?.label}
              placeholder={`Select ${owner_type_text?.label || ''}`}
              value={selectedOwner}
              onChange={(e) => {
                setSelectedOwner(e.target.value);
                onFormActivity?.();
              }}
            >
              {owner_list?.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                >
                  {item.label}
                </MenuItem>
              ))}
            </CustomSelect>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography
                variant='labelS'
                sx={{ color: '#666' }}
              >
                {owner_type_text?.label}
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#333' }}
              >
                {owner_list?.[0]?.label}
              </Typography>
            </Box>
          )}

          {phoneVerified ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  gap: 2,
                  alignItems: 'flex-end',
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant='labelS'
                    sx={{ color: '#666' }}
                  >
                    Mobile Number
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ color: '#333' }}
                  >
                    {mobileNumber}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flex: 1,
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <CheckCircleOutlinedIcon
                    sx={{ color: '#4CAF50', fontSize: 24 }}
                  />
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 400,
                      lineHeight: '20px',
                      color: '#000',
                    }}
                  >
                    Verified
                  </Typography>
                </Box>
              </Box>
              <IconButton
                sx={{ p: 0.5 }}
                onClick={() => {
                  setPhoneVerified(false);
                  setMobileNumber('');
                  onFormActivity?.();
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 16, color: '#995A00' }} />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <TextField
                label='Mobile number*'
                placeholder='Enter Mobile number'
                value={mobileNumber}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, '');
                  setMobileNumber(digitsOnly);
                  onFormActivity?.();
                  if (validationErrors.mobileNumber) {
                    setValidationErrors((prev) => {
                      const { mobileNumber, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                onBlur={() => {
                  if (
                    mobileNumber &&
                    !/^\d{10}$/.test(mobileNumber.replace(/\D/g, ''))
                  ) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      mobileNumber:
                        'Please enter a valid 10-digit phone number',
                    }));
                  }
                }}
                error={!!validationErrors.mobileNumber}
                helperText={validationErrors.mobileNumber}
                inputProps={{ maxLength: 10 }}
                sx={{ flex: 1 }}
              />
              <CustomButton
                variant='text'
                color='primary'
                label='Verify'
                onClick={handleVerifyPhone}
                disabled={otpSending || mobileNumber.length !== 10}
              />
            </Box>
          )}

          <TextField
            label='Email ID'
            placeholder='Please enter your email id'
            value={emailId}
            onChange={(e) => {
              setEmailId(e.target.value);
              onFormActivity?.();
              if (validationErrors.emailId) {
                setValidationErrors((prev) => {
                  const { emailId, ...rest } = prev;
                  return rest;
                });
              }
            }}
            onBlur={() => {
              if (emailId && !validateIsEmailAddress(emailId)) {
                setValidationErrors((prev) => ({
                  ...prev,
                  emailId: 'Invalid Email Address',
                }));
              }
            }}
            error={!!validationErrors.emailId}
            helperText={validationErrors.emailId}
          />
        </Box>
      </Box>

      {!digiLockerData && confirmation_text_for_digilocker?.show && (
        <CustomCheckbox
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            onFormActivity?.();
          }}
          label={checkboxLabel}
        />
      )}

      {validationErrors.digiLocker && (
        <Typography
          variant='body2'
          sx={{ color: 'error.main' }}
        >
          {validationErrors.digiLocker}
        </Typography>
      )}

      {!digiLockerData && (
        <CustomButton
          variant='filled'
          color='primary'
          label='Verify with Digilocker'
          disabled={
            digiLockerLoading ||
            (confirmation_text_for_digilocker?.show ? !termsAccepted : false) ||
            !phoneVerified
          }
          onClick={handleVerifyWithDigiLocker}
          sx={{ width: '100%' }}
        />
      )}

      {digiLockerLoading && !digiLockerData && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} sx={{ color: '#995A00' }} />
        </Box>
      )}
      {digiLockerData && <DigiLockerResultCard data={digiLockerData} />}

      {namesMismatch && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormHelperText error sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, m: 0 }}>
            <ErrorOutlineIcon sx={{ fontSize: 16, mt: '2px', flexShrink: 0 }} />
            {mismatchMessage}
          </FormHelperText>
          <CustomButton
            variant='filled'
            color='primary'
            label='Re-Verify'
            onClick={() => {
              setDigiLockerData(null);
              handleVerifyWithDigiLocker();
            }}
            sx={{ width: '100%' }}
          />
        </Box>
      )}

      {digiLockerData && (!namesMismatch || isMultiplePartnerMismatch) &&
        (panVerified ? (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='labelS'
                sx={{ color: '#666', fontWeight: 700 }}
              >
                PAN
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#333' }}
              >
                {panNumber}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              <CheckCircleOutlinedIcon
                sx={{ color: '#4CAF50', fontSize: 22 }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#000',
                }}
              >
                Verified
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              label='PAN*'
              placeholder='Enter PAN here'
              value={panNumber}
              onChange={(e) => {
                setPanNumber(e.target.value.toUpperCase());
                if (panError) setPanError(null);
              }}
              error={!!panError}
              helperText={panError}
              inputProps={{ maxLength: 10 }}
              disabled={isMultiplePartnerMismatch}
              sx={{ flex: 1 }}
            />
            <CustomButton
              variant='text'
              color='primary'
              label='Verify'
              onClick={handleVerifyPan}
              disabled={panVerifying || panNumber.length < 10 || isMultiplePartnerMismatch}
            />
          </Box>
        ))}

      {digiLockerData && (!namesMismatch || isMultiplePartnerMismatch) && (
        <CustomButton
          variant='filled'
          color='primary'
          label='Save and Continue'
          disabled={!panVerified || isMultiplePartnerMismatch}
          onClick={() => setConfirmModalOpen(true)}
          sx={{ width: '100%' }}
        />
      )}

      <GlassModal
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        title='Confirm Details'
        maxWidth='500px'
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='labelS'
                  sx={{ color: '#666', fontWeight: 700 }}
                >
                  Name
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ color: '#333' }}
                >
                  {digiLockerData?.name || '-'}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='labelS'
                  sx={{ color: '#666', fontWeight: 700 }}
                >
                  Gender
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ color: '#333' }}
                >
                  {formattedGender}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='labelS'
                  sx={{ color: '#666', fontWeight: 700 }}
                >
                  Date of Birth
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ color: '#333' }}
                >
                  {formatDobDisplay(digiLockerData?.date_of_birth)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant='labelS'
                  sx={{ color: '#666', fontWeight: 700 }}
                >
                  Age
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ color: '#333' }}
                >
                  {age ? `${age}y` : '-'}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography
                variant='labelS'
                sx={{ color: '#666', fontWeight: 700 }}
              >
                Mobile number
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#333' }}
              >
                {mobileNumber || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='labelS'
                sx={{ color: '#666', fontWeight: 700 }}
              >
                Email ID
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#333' }}
              >
                {emailId || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant='labelS'
                sx={{ color: '#666', fontWeight: 700 }}
              >
                PAN
              </Typography>
              <Typography
                variant='body1'
                sx={{ color: '#333' }}
              >
                {panNumber || '-'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: '24px',
                  color: '#333',
                }}
              >
                Confirm all details are correct.
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 400,
                  lineHeight: '20px',
                  color: '#333',
                }}
              >
                Are you sure you want to proceed?
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <CustomButton
                variant='outlined'
                color='primary'
                label='Edit'
                onClick={() => setConfirmModalOpen(false)}
                sx={{ flex: 1 }}
              />
              <CustomButton
                variant='filled'
                color='primary'
                label='Save'
                onClick={() => {
                  const ownerName =
                    owner_list?.find((o) => o.value === selectedOwner)?.label ||
                    owner_list?.[0]?.label;
                  const firmType = owner_type_text?.value;

                  dispatch(
                    setGapBeneficiaryData({
                      firm_owner_name: ownerName,
                      firm_type: firmType,
                      beneficiary: {
                        beneficiary_id: 0,
                        document_id: digiLockerData?.document_id || '',
                        pan: panNumber,
                        full_name: digiLockerData?.name || '',
                        gender: digiLockerData?.gender || '',
                        dob: digiLockerData?.date_of_birth,
                        phone_number: mobileNumber,
                        relation: 'Self',
                      },
                    }),
                  );
                  navigate('/beneficiary/add-gap-nominee', {
                    state: {
                      insuranceData: insuranceData,
                    },
                  });
                  setConfirmModalOpen(false);
                }}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </Box>
      </GlassModal>

      <GlassModal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        title='Verify Mobile Number'
        maxWidth='500px'
      >
        <Box sx={{ p: 2 }}>
          <OTPVerificationComponent
            mobileNumber={mobileNumber}
            onVerify={async (otp) => verifyBeneficiaryOtp(mobileNumber, otp)}
            onResend={async () => sendBeneficiaryOtp(mobileNumber)}
            onSuccess={() => {
              setPhoneVerified(true);
            }}
            onAnimationComplete={() => {
              setOtpModalOpen(false);
            }}
          />
        </Box>
      </GlassModal>

      <GlassModal
        open={kycConsentModalOpen}
        onClose={() => setKycConsentModalOpen(false)}
        title='KYC Consent'
      >
        <Box sx={{ p: 2 }}>
          <ModalList list={insuranceData?.kyc_consent_data || []} />
        </Box>
      </GlassModal>
    </Box>
  );
};

export default AddNpsBeneficiary;
